// Remove blacklisted links from HTML, while preserving inner text due to
// Google Payment policy (#16556, #16717)
export function replaceLinksForAndroid(text: string): string {
  text = text.replace(
    /<a\s+href=(?:\\?["'])https?:\/\/([^"'\/]*)(?:[^"']*)(?:\\?["'])[^>]*>(.*?)<\/a>/gi,
    replaceOneLink,
  )
  return text
}

/*
  Regex (above) to locate a hyperlink in HTML. This is one of the things
  you're supposed to never do. Let's do it!

  Note that this will *not* match internal links like <a href="#fragment">
  or <a href="/app">. We are only concerned with external links.

  <a\s+href=                # The literal "<a", followed by one or more spaces (\s+),
                            # and then the text "href=" (beginning of the href attribute)

  (?:\\?["'])               # An optional backslash (\\?) followed by either a double or single quote.
                            # This handles both normal quotes (" or ') and escaped quotes (\" or \').
                            # Because we have links with escaped quotes like `<a href=\"https://...\" target=\"_blank\">` in the backend response.

  https?:\/\/               # Protocol part of the URL.

  (                         # Capturing group for the domain part of the URL:
     [^"'\/]*               # Any characters that are not quotes or a slash.
  )

  (?:                       # Non-capturing group for the rest of the URL (including slashes):
     [^"']*                 # Any characters that are not quotes.
  )

  (?:\\?["'])               # The closing quote of the href attribute, optionally escaped.

  [^>]*                     # Any additional characters that are not ">" (other attributes or spaces)

  >                         # The closing ">" that ends the <a> opening tag

  (.*?)                     # Capture the link’s visible text — any characters, non-greedy, until the closing tag

  <\/a>                     # The literal closing tag "</a>"

  /gi                       # Flags:
                            #   g = global (replace all matches, not just the first)
                            #   i = case-insensitive (match <A>, <a>, etc.)
*/

// See the spreadsheet linked in #16717 for the source of these lists.
// We simplify heavily by using a couple of regex rules and a prefix
// list.

// Prefixes for domains to blacklist. (The domain must begin with one of
// these values.)
const androidBlacklistPrefix =
  /^(amazon\.|hachette|harpercollins|penguin|simonandschuster)/i

// An explicit list of domains to blacklist. (The entire domain must match
// one of these values.)
const androidBlacklistExplicit =
  /^(cambridge.org|affiliate-program.amazon.com|amazonseoconsultant.com|amzn.to|assets.cambridge.org|aws.amazon.com|beacon.org|bloomsbury.com|cambridge.org|cambridgescholars.com|elsevier.com|global.oup.com|harperbusiness.com|harperone.com|harperwave.com|hayhouse.com|milkweed.org|onlinelibrary.wiley.com|panmacmillan.com|pearson.com|store.macmillanlearning.com|us.macmillan.com|wiley.com|wwnorton.com)$/i

// Replacement callback for the regex above.
// If the domain is blacklisted, return the linktext. If not, return
// the entire original link (the match argument).
function replaceOneLink(
  match: string,
  domain: string,
  linktext: string,
): string {
  // Trim "www." off the start of the domain, since it's universal and not interesting.
  if (/^www[.]/i.test(domain)) {
    domain = domain.slice(4)
  }

  // Blacklist any domain that has "press" in the name, unless it's "wordpress" or "express".
  if (/(?<!word|ex)press/i.test(domain)) {
    return linktext
  }

  // Blacklist any domain that has "books" in the name, unless it's "books.google"
  if (/books(?!\.google)/i.test(domain)) {
    return linktext
  }

  // Blacklist any domain that has "shop" in the name, unless it's "workshop".
  if (/(?<!work)shop/i.test(domain)) {
    return linktext
  }

  // Look at the blacklist of prefixes.
  if (androidBlacklistPrefix.test(domain)) {
    return linktext
  }

  // Now look at the explicit blacklist.
  if (androidBlacklistExplicit.test(domain)) {
    return linktext
  }

  // Domain is okay; return the whole link.
  return match
}
